/**
 * F802 (Decision 467) - the company picker. A person's companies come from their DOCK grants (the API
 * decides; see src/lib/auth.ts verifyEntraToken). The browser only REMEMBERS which one they picked
 * (Pete F802: 'Remember last') and sends it on every API call as `x-symbia-company`.
 *
 * ⛔ A HINT, NEVER THE GATE: the API honours the header only when it names one of the person's own
 *    companies, and otherwise falls back to their default. Nothing here can widen access.
 * ⚠️ Browser storage can be missing (private window) - every read / write is wrapped; without it the
 *    API's default company is used.
 */
const KEY = 'bardotek.company';
export const COMPANY_HEADER = 'x-symbia-company';
export const COMPANY_QUERY_PARAM = 'company';

export function chosenCompanyId(): number | null {
    try {
        const n = Number(window.localStorage.getItem(KEY));
        return Number.isInteger(n) && n > 0 ? n : null;
    } catch { return null; }
}

export function rememberCompany(id: number): void {
    try { window.localStorage.setItem(KEY, String(id)); } catch { /* no storage - the API default is used */ }
}

/** `?company=CW` - set by a link from another app (the WMS header). Read once, by code. */
export function companyCodeFromSearch(search: string = window.location.search): string | null {
    try {
        const v = (new URLSearchParams(search).get(COMPANY_QUERY_PARAM) ?? '').trim().toUpperCase();
        return /^[A-Z0-9_-]{1,20}$/.test(v) ? v : null;
    } catch { return null; }
}

export function withoutCompanyParam(href: string = window.location.href): string | null {
    try {
        const u = new URL(href);
        if (!u.searchParams.has(COMPANY_QUERY_PARAM)) return null;
        u.searchParams.delete(COMPANY_QUERY_PARAM);
        return u.toString();
    } catch { return null; }
}

/** Adds the header to a request bound for our own API that carries a bearer token. */
export function addCompanyHeader(url: string, headers: Headers, apiBase: string, origin: string = window.location.origin): void {
    const id = chosenCompanyId();
    if (id === null || headers.has(COMPANY_HEADER) || !headers.has('Authorization')) return;
    let target: URL;
    try { target = new URL(url, origin); } catch { return; }
    let base: URL;
    try { base = new URL(apiBase, origin); } catch { return; }
    if (target.origin !== base.origin || !target.pathname.startsWith(base.pathname.replace(/\/$/, '') + '/')) return;
    headers.set(COMPANY_HEADER, String(id));
}

/**
 * Wraps window.fetch once so EVERY call to our API carries the picked company - the client helpers,
 * the admin screens' own fetches and the chat widget alike (one place, so none can forget it).
 */
export function installCompanyHeader(apiBase: string): void {
    const w = window as unknown as { __companyHeader?: boolean };
    if (w.__companyHeader) return;
    w.__companyHeader = true;
    const orig = window.fetch.bind(window);
    window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
        const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
        const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
        addCompanyHeader(url, headers, apiBase);
        if (!headers.has(COMPANY_HEADER)) return orig(input, init);
        return orig(input, { ...(init ?? {}), headers });
    };
}

/** Tab code -> platform app code (the header's greyed state). null = not a platform app. */
export const TAB_APP: Record<string, string | null> = { scheduler: 'DOCK', labor: 'LABOR', client: 'DOCK', carrier: null, pipeline: 'PIPELINE' };   // F810: the client portal is the Dock's own page
